/**
 * Product gallery enhancements:
 *
 * 1. Gallery videos stay playing unless the bottom-left pause button is
 *    clicked. Clicking the video cover opens the zoom modal (like images).
 *
 * 2. The theme's PhotoSwipe lightbox only builds slides from images. We
 *    patch openLightBox so videos become HTML slides. The already-playing
 *    gallery <video> is moved into the modal so playback is not blocked.
 */
(function () {
  const VIDEO_CELL = '.product-gallery__media[data-media-type="video"], .product-gallery__media[data-media-type="external_video"]';
  const TOGGLE_HTML =
    '<button type="button" class="product-gallery__video-toggle" data-video-toggle aria-label="Play or pause video">' +
      '<svg class="video-toggle__play" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 1.5l8 4.5-8 4.5v-9z" fill="currentColor"/></svg>' +
      '<svg class="video-toggle__pause" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 1.5h2.4v9H2.5v-9zm4.6 0h2.4v9H7.1v-9z" fill="currentColor"/></svg>' +
    '</button>';

  function slideSize(cell, video) {
    const frame = cell.querySelector('video-media') || cell;
    const frameW = frame.clientWidth;
    const frameH = frame.clientHeight;
    if (frameW > 10 && frameH > 10) {
      const scale = 2000 / frameH;
      return { width: Math.round(frameW * scale), height: Math.round(frameH * scale) };
    }

    const width = video?.videoWidth || parseInt(video?.getAttribute('width'), 10) || 1600;
    const height = video?.videoHeight || parseInt(video?.getAttribute('height'), 10) || 900;
    return { width, height };
  }

  function borrowVideo(cell) {
    const host = cell?.querySelector('video-media');
    const video = host?.querySelector('video');
    if (!video) return null;
    if (video.dataset.pswpBorrowed) return video;

    const placeholder = document.createElement('div');
    placeholder.className = 'pswp-video-placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    video.after(placeholder);

    video.dataset.pswpWidth = video.getAttribute('width') || '';
    video.dataset.pswpHeight = video.getAttribute('height') || '';
    video.removeAttribute('width');
    video.removeAttribute('height');
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.maxWidth = 'none';
    video.style.maxHeight = 'none';
    video.style.objectFit = 'cover';
    video.style.objectPosition = 'center';

    video.dataset.pswpBorrowed = '1';
    cell._videoPlaceholder = placeholder;
    cell._borrowedVideo = video;
    return video;
  }

  function restoreVideo(cell) {
    const video = cell?._borrowedVideo;
    const placeholder = cell?._videoPlaceholder;
    if (video && placeholder?.parentNode) {
      placeholder.replaceWith(video);
    } else if (video && !video.isConnected) {
      cell.querySelector('video-media')?.appendChild(video);
    }
    if (video) {
      if (video.dataset.pswpWidth) video.setAttribute('width', video.dataset.pswpWidth);
      if (video.dataset.pswpHeight) video.setAttribute('height', video.dataset.pswpHeight);
      delete video.dataset.pswpWidth;
      delete video.dataset.pswpHeight;
      video.style.width = '';
      video.style.height = '';
      video.style.maxWidth = '';
      video.style.maxHeight = '';
      video.style.objectFit = '';
      video.style.objectPosition = '';
      delete video.dataset.pswpBorrowed;
      video.muted = true;
      video.playsInline = true;
      if (!video.dataset.userPaused) {
        video.play().catch(() => {});
      }
    }
    delete cell._borrowedVideo;
    delete cell._videoPlaceholder;
  }

  function restoreAllBorrowed() {
    document.querySelectorAll(VIDEO_CELL).forEach((cell) => {
      if (cell._borrowedVideo) restoreVideo(cell);
    });
  }

  function playVideo(video) {
    if (!video || video.dataset.userPaused) return;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.loop = true;
    const tryPlay = () => {
      if (video.dataset.userPaused) return;
      const playPromise = video.play();
      if (playPromise && playPromise.catch) playPromise.catch(() => {});
    };
    tryPlay();
    if (video.readyState < 2) {
      video.addEventListener('loadeddata', tryPlay, { once: true });
      video.addEventListener('canplay', tryPlay, { once: true });
    }
  }

  function toggleHostedVideo(video) {
    if (!video) return;
    if (video.paused) {
      delete video.dataset.userPaused;
      playVideo(video);
    } else {
      video.dataset.userPaused = '1';
      video.pause();
    }
  }

  function bindSlideToggle(slide) {
    if (!slide || slide.dataset.videoToggleBound) return;
    const toggle = slide.querySelector('[data-video-toggle]');
    if (!toggle) return;
    slide.dataset.videoToggleBound = '1';

    const sync = () => {
      const video = slide.querySelector('video');
      slide.classList.toggle('is-playing', !!(video && !video.paused));
    };

    slide.addEventListener('play', sync, true);
    slide.addEventListener('pause', sync, true);
    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      toggleHostedVideo(slide.querySelector('video'));
    });
    sync();
  }

  function mountBorrowedVideo(slide, cell) {
    if (!slide || !cell) return;
    const video = cell._borrowedVideo || borrowVideo(cell);
    if (!video) return;

    if (!slide.contains(video)) {
      const dead = slide.querySelector('video');
      if (dead && dead !== video) dead.replaceWith(video);
      else slide.insertBefore(video, slide.firstChild);
    }

    playVideo(video);
    bindSlideToggle(slide);
  }

  function openVideoLightbox(cell) {
    const gallery = cell?.closest('product-gallery');
    if (!gallery?.openLightBox) return;
    gallery.openLightBox(null, cell);
  }

  document.addEventListener(
    'click',
    (event) => {
      const modalToggle = event.target.closest('.pswp__video-slide [data-video-toggle]');
      if (modalToggle) {
        event.preventDefault();
        event.stopPropagation();
        toggleHostedVideo(modalToggle.closest('.pswp__video-slide')?.querySelector('video'));
        return;
      }

      const cell = event.target.closest(VIDEO_CELL);
      if (!cell) return;

      if (event.target.closest('[data-video-toggle]')) {
        const videoMedia = cell.querySelector('video-media');
        if (!videoMedia) return;
        event.preventDefault();
        event.stopPropagation();
        if (videoMedia.hasAttribute('playing')) {
          videoMedia.pause();
        } else {
          videoMedia.play();
        }
        return;
      }

      event.stopPropagation();
      event.preventDefault();
      openVideoLightbox(cell);
    },
    true
  );

  function bindVideoPlayback(lightbox) {
    if (!lightbox || lightbox.__videoHooks) return;
    lightbox.__videoHooks = true;

    const syncVideos = () => {
      const pswp = lightbox.pswp;
      if (!pswp?.currSlide) return;
      const cell = pswp.currSlide.data?.galleryCell;
      const content = pswp.currSlide.content?.element;
      if (!cell || !content) return;

      let slide = content.querySelector('.pswp__video-slide');
      if (!slide) {
        slide = document.createElement('div');
        slide.className = 'pswp__video-slide is-playing';
        slide.insertAdjacentHTML('beforeend', TOGGLE_HTML);
        content.appendChild(slide);
      }

      mountBorrowedVideo(slide, cell);
    };

    lightbox.on('change', syncVideos);
    lightbox.on('openingAnimationEnd', syncVideos);
    lightbox.on('contentActivate', () => setTimeout(syncVideos, 0));
    lightbox.on('contentDestroy', (event) => {
      const cell = event.content?.data?.galleryCell;
      if (cell) restoreVideo(cell);
    });
    lightbox.on('close', restoreAllBorrowed);
  }

  function externalVideoHtml(cell) {
    const iframe = cell.querySelector('video-media iframe');
    const template = cell.querySelector('video-media template');
    const embed = iframe?.outerHTML || template?.innerHTML || '';
    if (!embed) return '';
    return `<div class="pswp__video-slide pswp__video-slide--embed is-playing">${embed}${TOGGLE_HTML}</div>`;
  }

  customElements.whenDefined('product-gallery').then(() => {
    const ProductGallery = customElements.get('product-gallery');
    if (ProductGallery.prototype.__videoLightbox) return;
    ProductGallery.prototype.__videoLightbox = true;

    ProductGallery.prototype.openLightBox = function (index, targetCell = null) {
      const dataSource = [];
      const imageSlideIndexes = [];
      const cellSlideIndexes = new Map();

      this.carousel.cells.forEach((cell) => {
        const mediaType = cell.getAttribute('data-media-type');

        if (mediaType === 'image') {
          const image = cell.querySelector(':scope > img');
          if (!image) return;
          cellSlideIndexes.set(cell, dataSource.length);
          imageSlideIndexes.push(dataSource.length);
          dataSource.push({
            thumbnailElement: image,
            src: image.src,
            srcset: image.srcset,
            msrc: image.currentSrc || image.src,
            width: parseInt(image.getAttribute('width')),
            height: parseInt(image.getAttribute('height')),
            alt: image.alt,
            thumbCropped: true,
          });
          return;
        }

        if (mediaType !== 'video' && mediaType !== 'external_video') return;

        const hosted = cell.querySelector('video-media video');
        if (!hosted && mediaType === 'external_video') {
          const html = externalVideoHtml(cell);
          if (!html) return;
          const size = slideSize(cell, null);
          cellSlideIndexes.set(cell, dataSource.length);
          dataSource.push({
            type: 'html',
            html,
            thumbnailElement: cell.querySelector('video-media'),
            thumbCropped: true,
            width: size.width,
            height: size.height,
          });
          return;
        }

        if (!hosted) return;

        const size = slideSize(cell, hosted);
        cellSlideIndexes.set(cell, dataSource.length);
        dataSource.push({
          type: 'html',
          html: `<div class="pswp__video-slide is-playing">${TOGGLE_HTML}</div>`,
          galleryCell: cell,
          thumbnailElement: cell.querySelector('video-media') || hosted,
          thumbCropped: true,
          width: size.width,
          height: size.height,
        });
      });

      let slideIndex;
      if (targetCell) {
        slideIndex = cellSlideIndexes.get(targetCell) ?? 0;
      } else if (index != null) {
        slideIndex = imageSlideIndexes[index] ?? 0;
      } else {
        slideIndex = cellSlideIndexes.get(this.carousel.selectedCell) ?? 0;
      }

      const lightbox = this.lightBox;
      bindVideoPlayback(lightbox);
      lightbox.loadAndOpen(slideIndex, dataSource);
    };
  });
})();
